import type {
  ApplyVendorPaymentRequest,
  ApplyVendorPaymentResponse,
  ExportVendorInvoicesRequest,
  MarkVendorInvoicesFailedRequest,
  VendorInvoiceFilterOptionsResponse,
  VendorInvoiceItem,
  VendorInvoicesQueryParams,
  VendorInvoicesResponse,
} from '@/types/vendorPayment.types';
import type { GenericErrorResponse } from '@brainvaulttech/dgr-react-sdk';
import { useMutation, useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { type AxiosError } from 'axios';
import { toast } from '@/components/ui/use-toast';
import { downloadFilefromBuffer } from '@/lib/utils';
import axiosInstance from './axiosInstance';

export const getVendorInvoicesData = async (params: VendorInvoicesQueryParams): Promise<VendorInvoicesResponse> => {
  const response = await axiosInstance.get<VendorInvoicesResponse>('/vendor-invoices', {
    params: {
      ...params,
      vendorIds: params.vendorIds?.length ? JSON.stringify(params.vendorIds) : undefined,
      invoiceNumbers: params.invoiceNumbers?.length ? JSON.stringify(params.invoiceNumbers) : undefined,
      checkNumbers: params.checkNumbers?.length ? JSON.stringify(params.checkNumbers) : undefined,
      fileNumbers: params.fileNumbers?.length ? JSON.stringify(params.fileNumbers) : undefined,
    },
  });
  return response.data;
};

export const useVendorInvoiceFilterOptions = (enabled = true) => {
  return useQuery<VendorInvoiceFilterOptionsResponse>({
    queryKey: ['vendor-invoices-filter-options'],
    queryFn: () =>
      axiosInstance.get<VendorInvoiceFilterOptionsResponse>('/vendor-invoices/filter-options').then((res) => res.data),
    staleTime: 1000 * 60 * 5,
    enabled,
  });
};

export const getVendorInvoices = (
  params: VendorInvoicesQueryParams,
  options?: { query?: Partial<UseQueryOptions<VendorInvoicesResponse>> }
): {
  data: VendorInvoiceItem[];
  totalRows: number;
  totalAmountDue?: string;
  lessThan30DaysDue?: string;
  past30DaysDue?: string;
  past60DaysDue?: string;
  past90DaysDue?: string;
  currentWeekDue?: string;
  invoiceNumbers?: string[];
  isFetching: boolean;
} => {
  const query = useQuery<VendorInvoicesResponse>({
    queryFn: () => getVendorInvoicesData(params),
    queryKey: ['vendor-invoices', params],
    ...options?.query,
  });

  const responseData = query.data;
  return {
    data: responseData?.data ?? [],
    totalRows: responseData?.totalRows ?? 0,
    totalAmountDue: responseData?.totalAmountDue,
    lessThan30DaysDue: responseData?.lessThan30DaysDue,
    past30DaysDue: responseData?.past30DaysDue,
    past60DaysDue: responseData?.past60DaysDue,
    past90DaysDue: responseData?.past90DaysDue,
    currentWeekDue: responseData?.currentWeekDue,
    invoiceNumbers: responseData?.invoiceNumbers,
    isFetching: query.isFetching,
  };
};

export const useApplyVendorPayment = () => {
  return useMutation<ApplyVendorPaymentResponse, AxiosError<GenericErrorResponse>, ApplyVendorPaymentRequest>({
    mutationFn: async (data) => {
      const response = await axiosInstance.post<ApplyVendorPaymentResponse>('/vendor-invoices/apply-payment', data);
      return response.data;
    },
  });
};

export const useMarkVendorInvoicesFailed = () => {
  return useMutation<{ message: string }, AxiosError<GenericErrorResponse>, MarkVendorInvoicesFailedRequest>({
    mutationFn: async (data) => {
      const response = await axiosInstance.post<{ message: string }>('/vendor-invoices/mark-failed', data);
      return response.data;
    },
  });
};

export const exportVendorInvoicesPdfs = async (
  request: ExportVendorInvoicesRequest,
  fallbackInvoiceNumber?: string
) => {
  toast({
    description: 'Preparing vendor invoices for export',
    duration: 3000,
  });
  try {
    const response = await axiosInstance.post<Blob>('/vendor-invoices/export', request, {
      responseType: 'blob',
    });

    const contentType = response.headers['content-type'] as string | undefined;
    const isZip = contentType?.includes('zip');

    const fileName = isZip ? 'vendor_invoices_export.zip' : `${fallbackInvoiceNumber ?? 'vendor_invoice'}.pdf`;

    downloadFilefromBuffer(response.data, fileName, isZip ? 'ZIP' : 'PDF');
  } catch {
    toast({
      description: 'Unable to export vendor invoices, please try again',
      duration: 3000,
      variant: 'destructive',
    });
  }
};
